class PlpRelatedLinks extends HTMLElement {
  constructor() {
    super();
    this.handleCollection = this.dataset.handleCollection;
    this.maxLinks = parseInt(this.dataset.maxLinks || '0', 10);
  }

  connectedCallback() {
    this.fetchData();
  }

  async fetchData() {
    if (!this.handleCollection) return;

    try {
      const response = await fetch(
        `https://api.wordlift.io/data/https/www.readers.com/collections/${this.handleCollection}`
      );

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const data = await response.json();

      // Find the CollectionPage object - @type can be a string or array
      const collectionPage = data.find((item) => {
        const type = item['@type'];
        if (Array.isArray(type)) {
          return type.some((t) => t.includes('CollectionPage'));
        }
        return type && type.includes('CollectionPage');
      });

      // Collect related links with labels from eyewear:categoryRelatedLinks
      let relatedLinks = [];

      if (collectionPage) {
        const categoryLinks = collectionPage['eyewear:categoryRelatedLinks'];
        if (Array.isArray(categoryLinks) && categoryLinks.length > 0) {
          // Sort by order and extract name + url
          relatedLinks = categoryLinks
            .sort((a, b) => {
              const orderA = parseInt(a['eyewear:categoryRelatedLinkOrder'] || '0', 10);
              const orderB = parseInt(b['eyewear:categoryRelatedLinkOrder'] || '0', 10);
              return orderA - orderB;
            })
            .map((entry) => ({
              url: entry['eyewear:categoryRelatedLinkUrl'] || '',
              name: (entry['eyewear:categoryRelatedLinkName'] || '').trim()
            }))
            .filter((entry) => entry.url);
        }

        // Fallback: use relatedLink (singular) array of URLs if no categoryRelatedLinks
        if (relatedLinks.length === 0 && Array.isArray(collectionPage.relatedLink)) {
          relatedLinks = collectionPage.relatedLink.map((url) => ({
            url,
            name: ''
          }));
        }
      }

      if (relatedLinks.length > 0) {
        // Limit to max links if configured
        const linksToRender = this.maxLinks > 0 ? relatedLinks.slice(0, this.maxLinks) : relatedLinks;
        this.renderLinks(linksToRender);
      }
      // Show component regardless (hidden attribute removed)
      this.removeAttribute('hidden');
    } catch (error) {
      this.removeAttribute('hidden');
      console.error('PlpRelatedLinks fetch error:', error);
    }
  }

  /**
   * Extracts a readable label from a collection URL.
   * e.g. "https://www.readers.com/collections/mens-reading-glasses" -> "Mens Reading Glasses"
   */
  getLabelFromUrl(url) {
    try {
      const pathname = new URL(url).pathname;
      // Get the last segment of the path
      const slug = pathname.split('/').filter(Boolean).pop() || '';
      // Convert slug to readable title
      return slug
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
    } catch {
      return url;
    }
  }

  renderLinks(links) {
    const list = this.querySelector('.plp-related-links__list');
    if (!list) return;

    links.forEach((linkData) => {
      const linkUrl = linkData.url;
      const linkName = linkData.name;

      // Convert full URL to relative path for internal navigation
      let href = linkUrl;
      try {
        const urlObj = new URL(linkUrl);
        href = urlObj.pathname;
      } catch {
        // Keep as-is if not a valid URL
      }

      // Use API-provided name, fallback to slug-based label
      const label = linkName || this.getLabelFromUrl(linkUrl);

      const li = document.createElement('li');
      li.className =
        'tw-w-fit tw-border-[.1rem] tw-border-[#45423d80] hover:tw-border-dark-grey tw-rounded-t-[2rem] tw-rounded-tr-[0] tw-rounded-b-[1.6rem] tw-rounded-l-[2rem] tw-group hover:tw-outline hover:tw-outline-1';

      const a = document.createElement('a');
      a.href = href;
      a.className =
        'tw-flex tw-gap-[1rem] tw-text-[1.4rem] tw-leading-[1.4rem] tw-text-[#45423D] tw-py-[.8rem] tw-px-[1.2rem] tw-whitespace-nowrap';

      const span = document.createElement('span');
      span.className = 'tw-whitespace-nowrap tw-text-ellipsis tw-overflow-hidden';
      span.textContent = label;

      a.appendChild(span);
      li.appendChild(a);
      list.appendChild(li);
    });
  }
}

if (!customElements.get('plp-related-links')) {
  customElements.define('plp-related-links', PlpRelatedLinks);
}
